import React from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { gql } from '@apollo/client';
import Breadcrumb from '../components/Breadcrumb';
import M3Card from '../components/M3Card';
import M3LinearProgress from '../components/M3Progress';
import { UPDATE_PUBLICATION, DELETE_PUBLICATION } from '../graphql/mutations';
import { PUBLICATIONS_QUERY } from '../graphql/queries';
import '../../css/_publication-show.css';

const PUBLICATION_QUERY = gql`
  query Publication($id: ID!) {
    publication(id: $id) {
      id
      title
      author
      year
      public
      createdAt
      user {
        name
      }
      pages {
        id
        number
        imageUrl
        figuresCount
      }
      figuresCount
    }
  }
`;

export default function PublicationShowPage() {
  const { id } = useParams();
  const { data, loading, error } = useQuery(PUBLICATION_QUERY, {
    variables: { id }
  });

  if (error)
    return (
      <div className="page-content">
        <p className="alert alert-danger">Error: {error.message}</p>
      </div>
    );

  if (loading)
    return (
      <div className="page-content">
        <M3LinearProgress indeterminate />
      </div>
    );

  const pub = data?.publication;
  if (!pub) return <div className="page-content">Publication not found.</div>;

  return (
    <div className="page-content detail-page">
      <Breadcrumb
        items={[{ label: 'Publications', link: '/publications' }, { label: pub.title }]}
      />

      <div className="detail-header">
        <h1>{pub.title}</h1>
        <div className="detail-actions">
          <Link to={`/publications/${id}/edit`} className="btn btn-secondary">
            <span className="material-symbols-rounded md-18">edit</span>
            Edit
          </Link>
        </div>
      </div>

      <M3Card variant="outlined">
        <M3Card.Header title="Information" />
        <M3Card.Body>
          <div className="detail-row">
            <span className="detail-label">Author</span>
            <span className="detail-value">{pub.author || '—'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Year</span>
            <span className="detail-value">{pub.year || '—'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Public</span>
            <span className="detail-value">{pub.public ? 'Yes' : 'No'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">User</span>
            <span className="detail-value">{pub.user?.name || '—'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Figures</span>
            <span className="detail-value">{pub.figuresCount || 0}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Created</span>
            <span className="detail-value">{new Date(pub.createdAt).toLocaleDateString()}</span>
          </div>
        </M3Card.Body>
      </M3Card>

      {pub.pages?.length > 0 && (
        <M3Card variant="outlined">
          <M3Card.Header
            title={`Pages (${pub.pages.length})`}
            action={
              <Link to={`/pages/${pub.pages[0].id}`} className="btn btn-sm btn-secondary">
                <span className="material-symbols-rounded">edit</span>
              </Link>
            }
          />
          <M3Card.Body>
            <div className="page-grid">
              {pub.pages.map((page) => (
                <Link key={page.id} to={`/pages/${page.id}`} className="page-card">
                  {page.imageUrl ? (
                    <img
                      src={page.imageUrl}
                      alt={`Page ${page.number + 1}`}
                      className="page-card-image"
                    />
                  ) : (
                    <div className="page-card-placeholder">
                      <span className="material-symbols-rounded">image</span>
                    </div>
                  )}
                  <div className="page-card-footer">
                    <span className="page-card-number">Page {page.number + 1}</span>
                    <span className="page-card-count">{page.figuresCount || 0} figures</span>
                  </div>
                </Link>
              ))}
            </div>
          </M3Card.Body>
        </M3Card>
      )}

      <div className="d-flex gap-2 mt-3">
        <Link to="/analytics" className="btn btn-primary">
          <span className="material-symbols-rounded md-18">insights</span>
          Analytics
        </Link>
      </div>
    </div>
  );
}
